import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';

import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {LINKS} from '@/constants/LinksConstants';
import {
  CMemberStatus,
  IRecordGroupExpenseRequest,
  ISettlementRequest,
} from '@/dataSource/src/api/groups/entities';
import {useGroupsRepository} from '@/dataSource/src/api/groups/useGroupsRepository';
import {getErrorSchema} from '@/hooks/TrackingManager/utils/getErrorSchema';
import {IErrorDataDefinition} from '@/services/segment/models/TrackingManagerEventModel';
import logError from '@/utils/errors/logError';
import {formatPhoneForDisplay} from '@/utils/formatPhoneForDisplay';
import {buildSplitGroupInviteLink} from '@/utils/goLinks/buildSplitGroupInviteLink';
import {isPhoneValid} from '@/utils/isPhoneValid';

import {SPLIT_BILLS_QUERY_PARAMS} from '../split-bills/constants';
import {
  OTP_LENGTH,
  RESEND_COUNTDOWN_SECONDS,
  SPLIT_REFERRAL_CODE,
} from '../splits/constants';

import {mapParticipantsToMembers} from './mappers/mapParticipantsToMembers';
import {mapSettlementToBalances} from './mappers/mapSettlementToBalances';
import {
  GROUPS_PAGES,
  GROUPS_QUERY_PARAMS,
  GROUPS_ROUTES,
  GROUPS_UTM,
  GROUPS_VIEW_STEP,
  GroupsViewStep,
  NEW_PARTICIPANT_ID,
} from './models/constants';
import {
  IBalancesUI,
  IGroupMember,
  IGroupParticipant,
  ISettlementMember,
} from './models/interfaces';
import {
  ISelectSplitsAddExpenseConfirmParams,
  useGroupsTrackers,
} from './trackers';

interface GroupsViewModelContextType {
  step: GroupsViewStep;
  canGoBack: boolean;
  inviterName: string;
  groupName: string;
  groupEmoji: string;
  onOpenAccount: () => void;
  onContinueFromLanding: () => void;
  isLoadingGroup: boolean;
  selectableParticipants: IGroupParticipant[];
  selectedParticipant: IGroupParticipant | null;
  onSelectParticipant: (participant: IGroupParticipant) => void;
  onContinueFromSelectParticipant: (participantName?: string) => void;
  handleBack: () => void;
  groupId?: string | null;
  phone: string;
  setPhone: (value: string) => void;
  isPhoneValid: boolean;
  onSubmitPhone: () => void;
  formattedPhone: string;
  otp: string;
  setOtp: (value: string) => void;
  resendSeconds: number;
  canResend: boolean;
  onResendOtp: () => void;
  onSubmitOtp: () => void;
  handleOpenAccountDashboard: () => void;
  handleShare: () => void;
  groupCurrentParticipant: IGroupParticipant | null;
  ownerGroupParticipant: IGroupParticipant | null;
  handleGoToNotFoundError: () => void;
  participants: IGroupParticipant[];
  handleAddExpense: () => void;
  handleAddExpenseManualConfirm: (
    payload: IRecordGroupExpenseRequest,
  ) => Promise<boolean>;
  balances?: IBalancesUI;
  members?: IGroupMember[];
  handleSettleMembers: (members: ISettlementMember[]) => Promise<void>;
  trackSelectSplitsAddExpense: () => void;
}

const fileName = 'GroupsViewModelContext.tsx';

const GroupsViewModelContext = createContext<
  GroupsViewModelContextType | undefined
>(undefined);

export function GroupsViewModelProvider({children}: {children: ReactNode}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {t} = useTranslation();

  const [step, setStep] = useState<GroupsViewStep>(GROUPS_VIEW_STEP.LANDING);
  const [selectedParticipant, setSelectedParticipant] =
    useState<IGroupParticipant | null>(null);
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [newParticipantName, setNewParticipantName] = useState<string>('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [resendSeconds, setResendSeconds] = useState(0);
  const formattedPhone = useMemo(() => formatPhoneForDisplay(phone), [phone]);
  const isPhoneValidFlag = useMemo(() => isPhoneValid(phone), [phone]);
  const canResend = resendSeconds === 0;

  const groupId = searchParams.get(GROUPS_QUERY_PARAMS.GROUP_ID);

  const {
    handleGetGroup,
    groupData,
    isLoadingGroup,
    isErrorGroup,
    handleJoinGroupStart,
    handleJoinGroupVerify,
    handleJoinGroupResend,
    handleConfirmSettlement,
    handleRecordAnonymousExpense,
  } = useGroupsRepository();

  const ownerModakUserId = groupData?.members?.find(
    m => m.split_user_id === groupData?.group?.owner_id,
  )?.modak_user_id;

  const participants = useMemo<IGroupParticipant[]>(() => {
    if (!groupData || !groupData.members) return [];
    return groupData.members.map(member => ({
      groupId: groupData.group.group_id,
      splitUserId: member.split_user_id,
      name: member.display_name,
      validated: member.status === CMemberStatus.ACTIVE,
      modakUserId: member.modak_user_id,
      balance: member.balance,
    }));
  }, [groupData]);

  const ownerGroupParticipant = useMemo<IGroupParticipant | null>(() => {
    if (!participants || participants.length === 0) return null;
    const owner = participants.find(
      member => member.splitUserId === groupData.group.owner_id,
    );
    if (!owner) return null;
    return {
      groupId: groupData.group.group_id,
      splitUserId: owner.splitUserId,
      name: owner.name,
      validated: owner.validated,
      modakUserId: owner.modakUserId,
      balance: owner.balance,
    };
  }, [groupData, participants]);

  const selectableParticipants = useMemo<IGroupParticipant[]>(() => {
    return participants.filter(
      participant =>
        participant.splitUserId !== ownerGroupParticipant?.splitUserId,
    );
  }, [participants, ownerGroupParticipant]);

  const inviterName = useMemo(() => {
    return (
      searchParams.get(GROUPS_QUERY_PARAMS.INVITER_NAME) ||
      ownerGroupParticipant?.name ||
      'Someone'
    );
  }, [searchParams, ownerGroupParticipant]);

  const groupEmoji = useMemo(() => {
    return groupData?.group.emoji || '👥';
  }, [groupData]);

  const groupName = useMemo(() => {
    return (
      searchParams.get(GROUPS_QUERY_PARAMS.GROUP_NAME) ||
      groupData?.group.name ||
      'Some'
    );
  }, [searchParams, groupData]);

  const splitParticipantId: string | undefined = useMemo(() => {
    return (
      participantId || searchParams.get(GROUPS_QUERY_PARAMS.PARTICIPANT_ID)
    );
  }, [participantId, searchParams]);

  const groupCurrentParticipant = useMemo<IGroupParticipant | null>(() => {
    const currentUser = participants?.find(
      member => member.splitUserId === splitParticipantId,
    );
    return (
      currentUser || {
        splitUserId: splitParticipantId || '',
        balance: 0,
        groupId: groupId || '',
        name: '',
        validated: false,
      }
    );
  }, [groupId, participants, splitParticipantId]);

  const {
    trackViewSplitsJoinGroup,
    trackViewSplitsGroup,
    trackViewSplitsJoinGroupFirstTime,
    trackViewSplitsOtpEnterCode,
    trackSelectSplitsJoinGroup,
    trackSelectSplitsSelectGroupMember,
    trackSelectSplitsMemberNotListed,
    trackSelectSplitsFirstMemberWritesName,
    trackSelectSplitsAddExpense,
    trackSelectSplitsAddExpenseConfirm,
    trackApiSplitsAddExpenseSuccess,
    trackApiSplitsAddExpenseError,
    trackSelectSplitsGetModak,
    trackSelectSplitsInviteMember,
    trackSelectSplitsOtpEnterCode,
    trackSelectSplitsPhoneNumberContinue,
    trackApiSplitsOtpSuccess,
    trackApiSplitsOtpError,
  } = useGroupsTrackers(
    groupId,
    ownerModakUserId,
    groupCurrentParticipant?.modakUserId,
  );

  const onOpenAccount = useCallback(() => {
    trackSelectSplitsGetModak();
    const url = LINKS.MODAK_GET_STARTED(
      GROUPS_UTM.INTRO.SOURCE,
      GROUPS_UTM.INTRO.MEDIUM,
      GROUPS_UTM.INTRO.CAMPAIGN,
      GROUPS_UTM.INTRO.CONTENT,
      SPLIT_REFERRAL_CODE,
    );
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      newWindow.focus();
      newWindow.opener = null;
    }
  }, [trackSelectSplitsGetModak]);

  const handleGroupsError = useCallback(
    err => {
      navigate(`${GROUPS_ROUTES.ERROR}?${searchParams.toString()}`, {
        state: {errorStatus: err?.status},
      });
    },
    [navigate, searchParams],
  );

  const handleGoToNotFoundError = useCallback(() => {
    handleGroupsError({status: 404});
  }, [handleGroupsError]);

  const fetchGroupData = useCallback(async () => {
    if (!groupId) {
      handleGoToNotFoundError();
      return;
    }
    try {
      await handleGetGroup(groupId);
    } catch (err) {
      handleGroupsError(err);
    }
  }, [groupId, handleGoToNotFoundError, handleGetGroup, handleGroupsError]);

  const onSelectParticipant = useCallback(
    (participant: IGroupParticipant) => {
      if (selectableParticipants?.length === 0) {
        trackSelectSplitsFirstMemberWritesName();
      } else if (participant.splitUserId === NEW_PARTICIPANT_ID) {
        trackSelectSplitsMemberNotListed();
      } else {
        trackSelectSplitsSelectGroupMember();
      }
      setSelectedParticipant(participant);
    },
    [
      selectableParticipants?.length,
      trackSelectSplitsFirstMemberWritesName,
      trackSelectSplitsMemberNotListed,
      trackSelectSplitsSelectGroupMember,
    ],
  );

  const handleSelectParticipantAndGoToDashboard = useCallback(
    (selectedParticipantId: string) => {
      setParticipantId(selectedParticipantId);
      searchParams.set(
        GROUPS_QUERY_PARAMS.PARTICIPANT_ID,
        selectedParticipantId,
      );
      navigate(`${GROUPS_ROUTES.DASHBOARD}?${searchParams.toString()}`, {
        state: {participantId: selectedParticipantId, groupId},
      });
    },
    [navigate, searchParams, groupId],
  );

  const onContinueFromLanding = useCallback(() => {
    trackSelectSplitsJoinGroup(groupId, groupName, groupEmoji);
    setStep(GROUPS_VIEW_STEP.SELECT_MEMBER);
  }, [trackSelectSplitsJoinGroup, groupId, groupName, groupEmoji]);

  const onContinueFromSelectParticipant = useCallback(
    async (participantName?: string) => {
      if (!selectedParticipant && !participantName) return;

      if (selectedParticipant?.validated) {
        handleSelectParticipantAndGoToDashboard(
          selectedParticipant.splitUserId,
        );
        return;
      }

      if (participantName) {
        setNewParticipantName(participantName);
      }

      setStep(GROUPS_VIEW_STEP.PHONE_INPUT);
    },
    [selectedParticipant, handleSelectParticipantAndGoToDashboard],
  );

  const onSubmitPhone = useCallback(async () => {
    if (!isPhoneValid(phone) || (!selectedParticipant && !newParticipantName))
      return;

    trackSelectSplitsPhoneNumberContinue();
    const isNewParticipant =
      selectedParticipant?.splitUserId === NEW_PARTICIPANT_ID;
    const response = await handleJoinGroupStart({
      display_name: isNewParticipant
        ? newParticipantName
        : selectedParticipant?.name,
      group_id: groupId,
      phone_number: phone,
      ...(selectedParticipant?.splitUserId &&
        !isNewParticipant && {
          split_user_id: selectedParticipant.splitUserId,
        }),
    });
    setParticipantId(response.split_user_id);
    setStep(GROUPS_VIEW_STEP.OTP);
    setOtp('');
    setResendSeconds(RESEND_COUNTDOWN_SECONDS);
  }, [
    phone,
    selectedParticipant,
    newParticipantName,
    trackSelectSplitsPhoneNumberContinue,
    handleJoinGroupStart,
    groupId,
  ]);

  const onResendOtp = useCallback(async () => {
    if (!canResend || !isPhoneValid(phone)) return;
    if (!groupId || !participantId) return;

    await handleJoinGroupResend({
      group_id: groupId,
      split_user_id: participantId,
    });

    setResendSeconds(RESEND_COUNTDOWN_SECONDS);
  }, [canResend, groupId, phone, participantId, handleJoinGroupResend]);

  const onSubmitOtp = useCallback(
    async (otpValue?: string) => {
      const code = otpValue ?? otp;
      if (code.length !== OTP_LENGTH) return;
      if (!groupId || !participantId) return;
      trackSelectSplitsOtpEnterCode();
      try {
        await handleJoinGroupVerify({
          group_id: groupId,
          split_user_id: participantId,
          code: code,
        });
        trackApiSplitsOtpSuccess();
        searchParams.set(GROUPS_QUERY_PARAMS.PARTICIPANT_ID, participantId);
        navigate(`${GROUPS_ROUTES.DASHBOARD}?${searchParams.toString()}`, {
          state: {participantId: participantId, groupId},
        });
      } catch {
        trackApiSplitsOtpError();
        return;
      }
    },
    [
      groupId,
      handleJoinGroupVerify,
      navigate,
      otp,
      participantId,
      searchParams,
      trackApiSplitsOtpError,
      trackApiSplitsOtpSuccess,
      trackSelectSplitsOtpEnterCode,
    ],
  );

  const handleOpenAccountDashboard = useCallback(() => {
    trackSelectSplitsGetModak();
    const url = LINKS.MODAK_GET_STARTED(
      GROUPS_UTM.DASHBOARD.SOURCE,
      GROUPS_UTM.DASHBOARD.MEDIUM,
      GROUPS_UTM.DASHBOARD.CAMPAIGN,
      GROUPS_UTM.DASHBOARD.CONTENT,
      SPLIT_REFERRAL_CODE,
    );
    window.open(url, '_blank');
  }, [trackSelectSplitsGetModak]);

  const canGoBack = useMemo(() => {
    return step !== GROUPS_VIEW_STEP.LANDING;
  }, [step]);

  const handleBack = useCallback(() => {
    switch (step) {
      case GROUPS_VIEW_STEP.OTP:
        setStep(GROUPS_VIEW_STEP.PHONE_INPUT);
        break;
      case GROUPS_VIEW_STEP.PHONE_INPUT:
        setStep(GROUPS_VIEW_STEP.SELECT_MEMBER);
        break;
      case GROUPS_VIEW_STEP.SELECT_MEMBER:
        setStep(GROUPS_VIEW_STEP.LANDING);
        break;
    }
  }, [step]);

  const shareUrl = useMemo(() => {
    if (groupId && groupCurrentParticipant) {
      return buildSplitGroupInviteLink(groupId, groupCurrentParticipant.name);
    }
  }, [groupId, groupCurrentParticipant]);

  const balances = useMemo<IBalancesUI | undefined>(() => {
    if (!groupData || !groupData.who_pays_whom || !groupData.members) {
      return {
        owed: {amount: 0, members: []},
        owe: {amount: 0, members: []},
      };
    }
    return mapSettlementToBalances(
      splitParticipantId || '',
      groupData.who_pays_whom,
      groupData.members,
    );
  }, [groupData, splitParticipantId]);

  const members = useMemo<IGroupMember[]>(() => {
    return mapParticipantsToMembers({
      participants,
      currentUserId: splitParticipantId || '',
      ownerId: groupData?.group.owner_id,
      t,
    });
  }, [groupData?.group.owner_id, participants, splitParticipantId, t]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSnackbar({
        summary: t('groups.dashboard.invite_snackbar.title'),
        message: t('groups.dashboard.invite_snackbar.message'),
        variant: SnackbarType.SUCCESS,
      });
    } catch (error) {
      logError(error, {
        fileName,
        functionName: 'handleCopy',
        customTitle: 'Groups - Error copying split link',
        data: {shareUrl},
      });
    }
  }, [shareUrl, t]);

  const handleShare = useCallback(async () => {
    trackSelectSplitsInviteMember();
    try {
      if (navigator.share) {
        const shareTitle = t('split_bills_page.success.join_group_split_title');

        const shareText = t('split_bills_page.success.join_group_split_text', {
          groupName: groupName ?? groupId,
          goLink: shareUrl,
          interpolation: {escapeValue: false},
        });

        await navigator.share({
          title: shareTitle,
          text: shareText,
        });
        return;
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        logError(error, {
          fileName,
          functionName: 'handleShare',
          customTitle: 'Groups - Error sharing split link',
          data: {shareUrl},
        });
      }
    }

    handleCopy();
  }, [
    trackSelectSplitsInviteMember,
    handleCopy,
    t,
    groupName,
    groupId,
    shareUrl,
  ]);

  const handleAddExpenseManualConfirm = useCallback(
    async (payload: IRecordGroupExpenseRequest): Promise<boolean> => {
      const objectToTrack: ISelectSplitsAddExpenseConfirmParams = {
        groupId: groupId || '',
        groupName: groupName || '',
        expenseTitle: payload.description || '',
        expenseAmount: (payload.detail.total ?? '').toString(),
        expenseDate: payload.expense_date || '',
        expensePayerId: payload.payers?.[0]?.split_user_id || '',
      };
      trackSelectSplitsAddExpenseConfirm(objectToTrack);
      try {
        await handleRecordAnonymousExpense(groupId, payload);
        trackApiSplitsAddExpenseSuccess(objectToTrack);
        return true;
      } catch (error) {
        const errorSchema = getErrorSchema(error);
        const errorData: IErrorDataDefinition = {
          code: errorSchema?.error.code,
          message: errorSchema?.error.message,
        };
        trackApiSplitsAddExpenseError({...objectToTrack, error: errorData});
        return false;
      }
    },
    [
      groupId,
      groupName,
      trackSelectSplitsAddExpenseConfirm,
      handleRecordAnonymousExpense,
      trackApiSplitsAddExpenseSuccess,
      trackApiSplitsAddExpenseError,
    ],
  );

  const handleAddExpense = useCallback(() => {
    trackSelectSplitsAddExpense();
    searchParams.set(SPLIT_BILLS_QUERY_PARAMS.GROUP_NAME, groupName || '');
    searchParams.set(
      SPLIT_BILLS_QUERY_PARAMS.INVITER_NAME,
      groupCurrentParticipant?.name || '',
    );
    navigate(`/split-bills?${searchParams.toString()}`);
  }, [
    trackSelectSplitsAddExpense,
    searchParams,
    groupName,
    groupCurrentParticipant?.name,
    navigate,
  ]);

  const handleSettleMembers = useCallback(
    async (members: ISettlementMember[]) => {
      const payload: ISettlementRequest = {
        settlements: members.map(member => ({
          from: member.from,
          to: member.to,
          amount: member.amount,
          group_id: groupId,
          settled_by: groupCurrentParticipant?.splitUserId || '',
        })),
      };
      await handleConfirmSettlement(groupId, payload);
    },
    [groupCurrentParticipant?.splitUserId, groupId, handleConfirmSettlement],
  );

  useEffect(() => {
    if (!groupId && !splitParticipantId) {
      handleGroupsError({status: 404});
      return;
    }
    fetchGroupData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, splitParticipantId]);

  useEffect(() => {
    if (step !== GROUPS_VIEW_STEP.OTP || resendSeconds <= 0) return;
    const id = setInterval(() => {
      setResendSeconds(s => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [step, resendSeconds]);

  const currentPage = useMemo(() => {
    switch (location.pathname) {
      case GROUPS_ROUTES.DASHBOARD:
        trackViewSplitsGroup(groupId);
        return GROUPS_PAGES.DASHBOARD;
      case GROUPS_ROUTES.SUCCESS:
        return GROUPS_PAGES.SUCCESS;
      case GROUPS_ROUTES.ERROR:
        return GROUPS_PAGES.ERROR;
      default:
        return GROUPS_PAGES.LANDING;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!groupId || isErrorGroup || currentPage !== GROUPS_PAGES.LANDING)
      return;
    switch (step) {
      case GROUPS_VIEW_STEP.LANDING:
        trackViewSplitsJoinGroup(groupId, groupName, groupEmoji);
        break;
      case GROUPS_VIEW_STEP.SELECT_MEMBER:
        break;
      case GROUPS_VIEW_STEP.PHONE_INPUT:
        trackViewSplitsJoinGroupFirstTime();
        break;
      case GROUPS_VIEW_STEP.OTP:
        trackViewSplitsOtpEnterCode();
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, groupId]);

  const value = useMemo<GroupsViewModelContextType>(
    () => ({
      step,
      canGoBack,
      inviterName,
      groupName,
      groupEmoji,
      isLoadingGroup,
      selectableParticipants,
      selectedParticipant,
      groupId,
      phone,
      setPhone,
      isPhoneValid: isPhoneValidFlag,
      onSubmitPhone,
      formattedPhone,
      otp,
      setOtp,
      resendSeconds,
      canResend,
      groupCurrentParticipant,
      ownerGroupParticipant,
      participants,
      balances,
      members,
      onResendOtp,
      onSubmitOtp,
      onContinueFromSelectParticipant,
      onSelectParticipant,
      onOpenAccount,
      onContinueFromLanding,
      handleBack,
      handleOpenAccountDashboard,
      handleShare,
      handleGoToNotFoundError,
      handleAddExpense,
      handleAddExpenseManualConfirm,
      handleSettleMembers,
      trackSelectSplitsAddExpense,
    }),
    [
      step,
      canGoBack,
      inviterName,
      groupName,
      groupEmoji,
      isLoadingGroup,
      selectableParticipants,
      selectedParticipant,
      groupId,
      phone,
      isPhoneValidFlag,
      onSubmitPhone,
      formattedPhone,
      otp,
      resendSeconds,
      canResend,
      groupCurrentParticipant,
      ownerGroupParticipant,
      participants,
      balances,
      members,
      onResendOtp,
      onSubmitOtp,
      onContinueFromSelectParticipant,
      onSelectParticipant,
      onOpenAccount,
      onContinueFromLanding,
      handleBack,
      handleOpenAccountDashboard,
      handleShare,
      handleGoToNotFoundError,
      handleAddExpense,
      handleAddExpenseManualConfirm,
      handleSettleMembers,
      trackSelectSplitsAddExpense,
    ],
  );
  return (
    <GroupsViewModelContext.Provider value={value}>
      {children}
    </GroupsViewModelContext.Provider>
  );
}

export const useGroupsViewModelProvider = () => {
  const context = useContext(GroupsViewModelContext);
  if (!context) {
    throw new Error(
      'useGroupsViewModelProvider must be used within GroupsViewModelProvider',
    );
  }
  return context;
};
